import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useNavigate } from "react-router-dom";
import { useToast } from "../hooks/use-toast";
import {
  Clock,
  User,
  Phone,
  MessageSquare,
  AlertCircle,
  RefreshCw,
  Plus,
  Search,
  Building2,
  Loader2,
  Link2,
  Inbox,
  Users,
} from "lucide-react";
import {
  queueAPI,
  usersAPI,
  clientsAPI,
  manualTicketAPI,
  ticketsAPI,
} from "../services/api";
import { formatWhatsAppNumber } from "@/utils/formatters";
import { isValidWhatsApp } from "@/utils/validators";
import ClientNameChoiceDialog from "../components/ClientNameChoiceDialog";
import { getExternalClientName } from "../utils/clientNames";

const Queue = () => {
  const [pendingTickets, setPendingTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [takingTicket, setTakingTicket] = useState(null);
  const [onlineCount, setOnlineCount] = useState(0);
  const navigate = useNavigate();
  const { toast } = useToast();
  const pollingRef = useRef(null);

  const [showNewTicketModal, setShowNewTicketModal] = useState(false);
  const [externalClients, setExternalClients] = useState([]);
  const [externalApiEnabled, setExternalApiEnabled] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false);
  const [localClients, setLocalClients] = useState([]);
  const [loadingLocalClients, setLoadingLocalClients] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  const [clientSourceFilter, setClientSourceFilter] = useState("whatsapp");
  const [selectedClient, setSelectedClient] = useState(null);
  const [newTicketForm, setNewTicketForm] = useState({
    client_name: "",
    client_phone: "",
    description: "",
    priority: "medium",
    category: "",
  });
  const [creatingTicket, setCreatingTicket] = useState(false);

  const [showLinkClientModal, setShowLinkClientModal] = useState(false);
  const [linkingTicketId, setLinkingTicketId] = useState(null);
  const [linkClientSearch, setLinkClientSearch] = useState("");
  const [linkSelectedClient, setLinkSelectedClient] = useState(null);
  const [linkingClient, setLinkingClient] = useState(false);
  const [showClientNameChoice, setShowClientNameChoice] = useState(false);
  const [linkingClientName, setLinkingClientName] = useState("");

  useEffect(() => {
    loadPendingTickets();
    loadOnlineCount();
    sendHeartbeat();
    checkExternalApiEnabled();

    pollingRef.current = setInterval(() => {
      loadPendingTickets();
      sendHeartbeat();
    }, 5000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  const checkExternalApiEnabled = async () => {
    try {
      const response = await clientsAPI.getExternal();
      if (response.data.enabled) {
        setExternalApiEnabled(true);
        setExternalClients(response.data.clients || []);
      }
    } catch (error) {
      console.error("Error checking external API:", error);
    }
  };

  const sendHeartbeat = async () => {
    try {
      await usersAPI.heartbeat();
    } catch (error) {
      console.error("Heartbeat error:", error);
    }
  };

  const loadOnlineCount = async () => {
    try {
      const response = await usersAPI.getOnline();
      setOnlineCount(response.data.length);
    } catch (error) {
      console.error("Error loading online users:", error);
    }
  };

  const loadExternalClients = async () => {
    setLoadingClients(true);
    try {
      const response = await clientsAPI.getExternal();
      if (response.data.enabled) {
        setExternalApiEnabled(true);
        setExternalClients(response.data.clients || []);
      } else {
        setExternalApiEnabled(false);
        setExternalClients([]);
      }
    } catch (error) {
      console.error("Error loading external clients:", error);
      setExternalApiEnabled(false);
    } finally {
      setLoadingClients(false);
    }
  };

  const loadLocalClientsForModal = async () => {
    setLoadingLocalClients(true);
    try {
      const response = await clientsAPI.getAll();

      let allLocals = [];
      if (Array.isArray(response.data)) {
        allLocals = response.data;
      } else if (response.data?.items) {
        allLocals = response.data.items;
      } else if (response.data?.clients) {
        allLocals = response.data.clients;
      }

      const validLocals = allLocals.filter((c) => {
        const phone = c.real_phone || c.whatsapp_id || c.phone || c.whatsapp;
        return isValidWhatsApp(phone);
      });

      setLocalClients(validLocals);
    } catch (error) {
      console.error("Erro ao carregar clientes locais", error);
    } finally {
      setLoadingLocalClients(false);
    }
  };

  const combinedClients = [
    ...externalClients.map((c) => ({
      ...c,
      isLocal: false,
      displayId: `ext-${c.id || c.codigo}`,
    })),
    ...localClients.map((c) => ({
      ...c,
      isLocal: true,
      displayId: `loc-${c.id || c.whatsapp_id || c.phone}`,
    })),
  ];

  const filteredClientsList = combinedClients.filter((c) => {
    if (clientSourceFilter === "external" && c.isLocal) return false;
    if (clientSourceFilter === "whatsapp" && !c.isLocal) return false;

    if (!clientSearch) return true;

    const term = String(clientSearch).toLowerCase();
    return (
      String(c.name || "")
        .toLowerCase()
        .includes(term) ||
      String(c.company || "")
        .toLowerCase()
        .includes(term) ||
      String(c.razao_social || "")
        .toLowerCase()
        .includes(term) ||
      String(c.nome_fantasia || "")
        .toLowerCase()
        .includes(term) ||
      String(c.cnpj || "").includes(term) ||
      String(
        c.whatsapp || c.phone || c.whatsapp_id || c.real_phone || "",
      ).includes(term)
    );
  });

  const openNewTicketModal = () => {
    setShowNewTicketModal(true);
    setSelectedClient(null);
    setClientSearch("");
    setNewTicketForm({
      client_name: "",
      client_phone: "",
      description: "",
      priority: "medium",
      category: "Suporte Técnico",
    });
    loadExternalClients();
    loadLocalClientsForModal();
  };

  const handleSelectClientForNewTicket = (client) => {
    let phoneToUse = "";
    if (client.isLocal) {
      phoneToUse =
        client.real_phone || client.whatsapp_id || client.phone || "";
      phoneToUse = phoneToUse.split("@")[0];
    } else {
      phoneToUse = client.whatsapp || client.phone || "";
    }

    setNewTicketForm({
      ...newTicketForm,
      client_name:
        client.name || client.razao_social || client.nome_fantasia || "",
      client_phone: phoneToUse.replace(/\D/g, ""),
    });
  };

  const clearSelectedClient = () => {
    setSelectedClient(null);
    setNewTicketForm({
      ...newTicketForm,
      client_name: "",
      client_phone: "",
    });
  };

  const handleCreateManualTicket = async () => {
    if (
      !newTicketForm.client_name ||
      !newTicketForm.client_phone ||
      !newTicketForm.category
    ) {
      toast({
        title: "Erro",
        description: "Nome, telefone e Categoria são obrigatórios.",
        variant: "destructive",
      });
      return;
    }

    setCreatingTicket(true);
    try {
      const cleanPhone = newTicketForm.client_phone.replace(/\D/g, "");

      const payload = {
        client_name: newTicketForm.client_name,
        client_phone: cleanPhone,
        description: newTicketForm.description,
        priority: newTicketForm.priority,
        category: newTicketForm.category,
      };

      if (selectedClient) {
        payload.external_client_id = selectedClient.id;
        payload.external_client_data =
          selectedClient.raw_data || selectedClient;
      }

      const response = await manualTicketAPI.create(payload);

      toast({
        title: "Atendimento criado!",
        description: `Protocolo: ${response.data.protocol}`,
      });

      setShowNewTicketModal(false);

      navigate(`/chat/${response.data.id}`);
    } catch (error) {
      const errorMsg =
        error.response?.data?.detail || "Erro ao criar atendimento.";
      toast({
        title: "Erro",
        description: errorMsg,
        variant: "destructive",
      });
    } finally {
      setCreatingTicket(false);
    }
  };

  const filteredClients = externalClients.filter((client) => {
    const search = clientSearch.toLowerCase();
    return (
      (client.name || "").toLowerCase().includes(search) ||
      (client.razao_social || "").toLowerCase().includes(search) ||
      (client.nome_fantasia || "").toLowerCase().includes(search) ||
      (client.whatsapp || "").toLowerCase().includes(search) ||
      (client.phone || "").toLowerCase().includes(search) ||
      (client.cnpj || "").toLowerCase().includes(search) ||
      (client.email || "").toLowerCase().includes(search)
    );
  });

  const filteredLinkClients = externalClients.filter((client) => {
    const search = linkClientSearch.toLowerCase();
    return (
      (client.name || "").toLowerCase().includes(search) ||
      (client.razao_social || "").toLowerCase().includes(search) ||
      (client.nome_fantasia || "").toLowerCase().includes(search) ||
      (client.whatsapp || "").toLowerCase().includes(search) ||
      (client.phone || "").toLowerCase().includes(search) ||
      (client.cnpj || "").toLowerCase().includes(search) ||
      (client.email || "").toLowerCase().includes(search)
    );
  });

  const openLinkClientModal = (ticketId) => {
    setLinkingTicketId(ticketId);
    setLinkingClientName(pendingTickets.find((item) => item.id === ticketId)?.client_name || "");
    setShowClientNameChoice(false);
    setLinkClientSearch("");
    setLinkSelectedClient(null);
    setShowLinkClientModal(true);
  };

  const handleLinkClient = async (updateClientName = null) => {
    if (linkingClient) return;
    if (!linkSelectedClient) {
      toast({
        title: "Erro",
        description: "Selecione um cliente para vincular.",
        variant: "destructive",
      });
      return;
    }

    const companyName = getExternalClientName(linkSelectedClient);
    if (updateClientName === null && companyName && companyName !== linkingClientName.trim()) {
      setShowClientNameChoice(true);
      return;
    }

    setShowClientNameChoice(false);
    setLinkingClient(true);
    try {
      await ticketsAPI.linkClient(linkingTicketId, {
        external_client_id: linkSelectedClient.id,
        external_client_data: linkSelectedClient.raw_data || linkSelectedClient,
        update_client_name: updateClientName === true,
      });

      toast({
        title: "Cliente vinculado!",
        description: `Cliente ${linkSelectedClient.razao_social || linkSelectedClient.name} vinculado com sucesso.`,
      });

      setShowLinkClientModal(false);
      loadPendingTickets();
    } catch (error) {
      toast({
        title: "Erro",
        description:
          error.response?.data?.detail || "Erro ao vincular cliente.",
        variant: "destructive",
      });
    } finally {
      setLinkingClient(false);
    }
  };

  const loadPendingTickets = async () => {
    try {
      const response = await queueAPI.getPending();

      const filaReal = response.data.filter(
        (t) => t.menu_response_received === true,
      );

      setPendingTickets(filaReal);
    } catch (error) {
      console.error("Error loading pending tickets:", error);
    }
  };

  const handleTakeTicket = async (ticketId) => {
    setTakingTicket(ticketId);
    setLoading(true);

    try {
      await queueAPI.takeTicket(ticketId);

      toast({
        title: "Atendimento iniciado!",
        description: "Você assumiu este atendimento.",
      });

      navigate(`/chat/${ticketId}`);
    } catch (error) {
      const errorMsg =
        error.response?.data?.detail || "Erro ao pegar atendimento.";
      toast({
        title: "Erro",
        description: errorMsg,
        variant: "destructive",
      });
      loadPendingTickets();
    } finally {
      setLoading(false);
      setTakingTicket(null);
    }
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const created = new Date(timestamp);
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return "Agora mesmo";
    if (diffMins < 60) return `${diffMins} min atrás`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h atrás`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d atrás`;
  };

  const priorityColors = {
    low: "bg-green-100 text-green-800",
    medium: "bg-yellow-100 text-yellow-800",
    high: "bg-orange-100 text-orange-800",
    urgent: "bg-red-100 text-red-800",
  };

  const priorityLabels = {
    low: "Baixa",
    medium: "Média",
    high: "Alta",
    urgent: "Urgente",
  };

  return (
    <div className="space-y-6" data-testid="queue-page">
      <div className="flex flex-col sm:flex-row justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex gap-2 text-gray-800">
            <Inbox className="w-8 h-8 text-red-600 shrink-0" />
            Fila de Atendimentos
          </h1>
          <div className="flex flex-col sm:flex-row items-start gap-2 sm:gap-4 mt-1 mb-4">
            <p className="text-gray-600">
              {pendingTickets.length} atendimento(s) aguardando na fila
            </p>
            <Badge
              variant="outline"
              className="text-green-600 border-green-600"
            >
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
              {onlineCount} atendente(s) online
            </Badge>
          </div>
        </div>

        <div className="w-4/5 sm:w-auto flex flex-col xl:flex-row self-center gap-2">
          <Button
            onClick={openNewTicketModal}
            className="bg-green-600 hover:bg-green-700"
            data-testid="new-manual-ticket-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Atendimento Avulso
          </Button>
          <Button
            onClick={() => {
              loadPendingTickets();
              loadOnlineCount();
            }}
            variant="outline"
            data-testid="refresh-queue-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar Fila
          </Button>
        </div>
      </div>

      <Dialog open={showNewTicketModal} onOpenChange={setShowNewTicketModal}>
        <DialogContent className="w-full max-w-[95vw] lg:max-w-4xl max-h-[95dvh] p-4 sm:p-6 overflow-y-auto overflow-x-hidden rounded-xl sm:rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl sm:text-2xl">
              Novo Atendimento Avulso
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Crie um novo atendimento manualmente. Selecione um cliente do
              Histórico/API ou preencha manualmente.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="space-y-4 w-full">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-600 shrink-0" />
                  <span className="truncate">
                    Contatos Disponíveis ({combinedClients.length})
                  </span>
                </Label>
                {newTicketForm.client_name && newTicketForm.client_phone && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setNewTicketForm({
                        ...newTicketForm,
                        client_name: "",
                        client_phone: "",
                      });
                      setSelectedClient(null);
                      setClientSearch("");
                    }}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
                  >
                    Limpar dados
                  </Button>
                )}
              </div>

              {loadingClients || loadingLocalClients ? (
                <div className="flex items-center justify-center py-8 border rounded-lg bg-gray-50 w-full">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
                  <span className="ml-2 text-gray-500">
                    Carregando contatos...
                  </span>
                </div>
              ) : (
                <>
                  <div className="flex flex-col sm:flex-row gap-3 w-full">
                    <div className="w-full sm:w-1/3 shrink-0">
                      <Select
                        value={clientSourceFilter}
                        onValueChange={setClientSourceFilter}
                      >
                        <SelectTrigger className="bg-white w-full">
                          <SelectValue placeholder="Fonte" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="whatsapp">
                            📱 WhatsApp (Salvos)
                          </SelectItem>
                          <SelectItem value="external">
                            🏢 API Externa
                          </SelectItem>
                          <SelectItem value="all">
                            🌍 Todos os Contatos
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="relative flex-1 w-full">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Buscar por razão social, nome, CNPJ, WhatsApp..."
                        value={clientSearch}
                        onChange={(e) => setClientSearch(e.target.value)}
                        className="pl-10 focus-visible:ring-blue-500 bg-white w-full"
                        data-testid="client-search-input"
                      />
                    </div>
                  </div>

                  <div className="h-64 overflow-y-auto overflow-x-hidden border rounded-lg bg-gray-50 shadow-inner w-full">
                    {filteredClientsList.length === 0 ? (
                      <div className="p-4 text-center text-gray-500 mt-10">
                        {clientSearch
                          ? "Nenhum cliente encontrado"
                          : "Nenhum cliente disponível nesta fonte"}
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {filteredClientsList.slice(0, 100).map((client) => {
                          const rawPhone = client.isLocal
                            ? (
                                client.real_phone ||
                                client.whatsapp_id ||
                                client.phone ||
                                ""
                              ).split("@")[0]
                            : client.whatsapp || client.phone || "";

                          const isLid = client.isLocal && rawPhone.length >= 15;

                          return (
                            <div
                              key={client.displayId}
                              onClick={() =>
                                handleSelectClientForNewTicket(client)
                              }
                              className={`p-3 cursor-pointer transition-all duration-150 hover:bg-white hover:shadow-sm overflow-hidden ${isLid ? "bg-red-50/50 hover:bg-red-50" : ""}`}
                              data-testid={`client-option-${client.displayId}`}
                            >
                              <div className="flex flex-col gap-2 w-full min-w-0">
                                
                                {/* 👇 LINHA 1: NOME + BADGE (Flex-col no mobile, lado a lado no PC) */}
                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1.5 w-full min-w-0">
                                  <p
                                    className="font-bold text-gray-900 line-clamp-2 break-words leading-snug flex-1 min-w-0"
                                    title={
                                      client.razao_social ||
                                      client.name ||
                                      "Cliente"
                                    }
                                  >
                                    {client.razao_social ||
                                      client.name ||
                                      "Cliente"}
                                  </p>
                                  {client.isLocal ? (
                                    <Badge
                                      variant="outline"
                                      className="bg-green-50 text-green-700 border-green-200 text-[10px] sm:text-xs py-0 shrink-0 w-max"
                                    >
                                      WhatsApp
                                    </Badge>
                                  ) : (
                                    <Badge
                                      variant="outline"
                                      className="bg-purple-50 text-purple-700 border-purple-200 text-[10px] sm:text-xs py-0 shrink-0 w-max"
                                    >
                                      API Externa
                                    </Badge>
                                  )}
                                </div>

                                {/* 👇 LINHA 2: NOME FANTASIA E CNPJ (Quebra de linha no Fantasia) */}
                                {(client.nome_fantasia ||
                                  client.company ||
                                  client.cnpj) && (
                                  <div className="flex flex-col sm:flex-row sm:items-start gap-1.5 w-full min-w-0">
                                    {(client.nome_fantasia ||
                                      client.company) && (
                                      <span className="text-[12px] sm:text-[13px] text-blue-600 font-medium line-clamp-2 break-words flex-1 min-w-0 leading-snug">
                                        {client.nome_fantasia || client.company}
                                      </span>
                                    )}
                                    {client.cnpj && (
                                      <span className="text-[10px] sm:text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded shrink-0 w-max">
                                        {client.cnpj}
                                      </span>
                                    )}
                                  </div>
                                )}

                                {/* 👇 LINHA 3: TELEFONE E BADGE DE LID */}
                                <div className="flex items-center gap-1.5 w-full min-w-0 mt-0.5">
                                  <div className="flex items-center gap-1.5 flex-1 min-w-0 text-green-600">
                                    <Phone className="w-3 h-3 shrink-0" />
                                    <span className="text-xs sm:text-sm font-medium truncate min-w-0">
                                      {rawPhone}
                                    </span>
                                  </div>

                                  {isLid && (
                                    <Badge
                                      variant="outline"
                                      className="bg-red-100 text-red-700 border-red-200 text-[9px] sm:text-[10px] py-0 px-1.5 shrink-0"
                                      title="Cuidado: Iniciar conversa com um ID Oculto pode falhar."
                                    >
                                      ⚠️ ID Oculto
                                    </Badge>
                                  )}
                                </div>
                                
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {filteredClientsList.length > 100 && (
                    <p className="text-xs text-gray-500 text-center px-2">
                      Mostrando 100 de {filteredClientsList.length} resultados.
                      Refine sua busca para ver mais.
                    </p>
                  )}
                </>
              )}

              <div className="relative pt-2">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 font-semibold text-gray-400">
                    ou preencha manualmente
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="client_name" className="font-bold">
                  Nome / Empresa *
                </Label>
                <Input
                  id="client_name"
                  value={newTicketForm.client_name}
                  onChange={(e) =>
                    setNewTicketForm({
                      ...newTicketForm,
                      client_name: e.target.value,
                    })
                  }
                  placeholder="Nome completo do cliente"
                  data-testid="manual-client-name"
                  className={
                    newTicketForm.client_name
                      ? "bg-blue-50/30 border-blue-200"
                      : ""
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="client_phone" className="font-bold">
                  Telefone/WhatsApp *
                </Label>
                <Input
                  id="client_phone"
                  value={newTicketForm.client_phone}
                  onChange={(e) =>
                    setNewTicketForm({
                      ...newTicketForm,
                      client_phone: e.target.value.replace(/\D/g, ""),
                    })
                  }
                  placeholder="Ex: 5511999999999"
                  data-testid="manual-client-phone"
                  className={
                    newTicketForm.client_phone
                      ? "bg-blue-50/30 border-blue-200"
                      : ""
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="font-bold">
                Descrição/Motivo Inicial
              </Label>
              <Textarea
                id="description"
                value={newTicketForm.description}
                onChange={(e) =>
                  setNewTicketForm({
                    ...newTicketForm,
                    description: e.target.value,
                  })
                }
                placeholder="Descreva sobre o que o cliente quer falar..."
                rows={3}
                data-testid="manual-description"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category" className="font-bold">
                  Categoria / Setor *
                </Label>
                <Select
                  value={newTicketForm.category}
                  onValueChange={(value) =>
                    setNewTicketForm({ ...newTicketForm, category: value })
                  }
                >
                  <SelectTrigger data-testid="manual-category">
                    <SelectValue placeholder="Selecione o setor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Suporte Técnico">
                      Suporte Técnico
                    </SelectItem>
                    <SelectItem value="Financeiro">Financeiro</SelectItem>
                    <SelectItem value="Comercial">Comercial</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="priority" className="font-bold">
                  Prioridade
                </Label>
                <Select
                  value={newTicketForm.priority}
                  onValueChange={(value) =>
                    setNewTicketForm({ ...newTicketForm, priority: value })
                  }
                >
                  <SelectTrigger data-testid="manual-priority">
                    <SelectValue placeholder="Selecione a prioridade" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Baixa</SelectItem>
                    <SelectItem value="medium">Média</SelectItem>
                    <SelectItem value="high">Alta</SelectItem>
                    <SelectItem value="urgent">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter className="bg-gray-50 -mx-6 -mb-6 px-6 py-4 mt-2">
            <Button
              variant="outline"
              onClick={() => setShowNewTicketModal(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCreateManualTicket}
              disabled={
                creatingTicket ||
                !newTicketForm.client_name ||
                !newTicketForm.client_phone ||
                !newTicketForm.category
              }
              className="bg-green-600 hover:bg-green-700 shadow-md"
              data-testid="create-manual-ticket-btn"
            >
              {creatingTicket ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Criando...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Iniciar Atendimento
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {pendingTickets.length === 0 ? (
        <Card>
          <CardContent className="py-16">
            <div className="text-center text-gray-500">
              <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <p className="text-lg font-medium">Nenhum atendimento na fila</p>
              <p className="text-sm mt-2">
                Quando clientes selecionarem uma opção do menu, aparecerão aqui
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pendingTickets.map((ticket) => (
            <Card
              key={ticket.id}
              className="hover:shadow-lg transition-shadow"
              data-testid={`queue-ticket-${ticket.protocol}`}
            >
              <CardHeader>
                <div className="flex flex-col sm:flex-row items-center sm:items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <User className="w-5 h-5 text-blue-600 shrink-0" />
                      <span className="line-clamp-1 break-words">
                        {ticket.client_name}
                      </span>
                    </CardTitle>
                    <p className="text-sm text-gray-600 mt-1">
                      Protocolo: {ticket.protocol}
                    </p>
                  </div>
                  <Badge className={priorityColors[ticket.priority]}>
                    {priorityLabels[ticket.priority]}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {ticket.client_identified && (
                  <div className="p-3 bg-purple-50 rounded-lg border-l-4 border-purple-500">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold text-purple-600">
                        🔗 CLIENTE IDENTIFICADO
                      </span>
                      <Badge className="bg-purple-100 text-purple-700 text-xs">
                        API Externa
                      </Badge>
                    </div>
                    <p className="text-sm font-medium text-purple-900">
                      {ticket.external_client_name || ticket.client_name}
                    </p>
                    {ticket.external_client_company && (
                      <p className="text-xs text-purple-700">
                        🏢 {ticket.external_client_company}
                      </p>
                    )}
                    {ticket.external_client_email && (
                      <p className="text-xs text-purple-700">
                        📧 {ticket.external_client_email}
                      </p>
                    )}
                  </div>
                )}

                {ticket.initial_message && (
                  <div className="p-3 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                    <p className="text-xs font-semibold text-blue-600 mb-1">
                      📝 Motivo:
                    </p>
                    <p className="text-sm text-gray-800 line-clamp-2">
                      "{ticket.initial_message}"
                    </p>
                  </div>
                )}

                {ticket.selected_menu_option && (
                  <div className="p-2 bg-green-50 rounded-lg border-l-4 border-green-500">
                    <p className="text-xs font-semibold text-green-600">
                      ✅ {ticket.selected_menu_option}
                    </p>
                  </div>
                )}

                {!ticket.selected_menu_option && ticket.initial_message && (
                  <div className="p-2 bg-yellow-50 rounded-lg">
                    <p className="text-xs text-yellow-700">
                      ⏳ Aguardando seleção do menu
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <div className="flex items-center text-sm text-gray-600 overflow-hidden">
                    <Phone className="w-4 h-4 mr-2 flex-shrink-0 text-gray-400" />
                    <span
                      className="truncate font-medium"
                      title={ticket.client_phone}
                    >
                      {formatWhatsAppNumber(ticket.client_phone)}
                    </span>
                  </div>

                  <div className="flex items-center text-sm text-gray-600">
                    <Clock className="w-4 h-4 mr-2" />
                    {getTimeAgo(ticket.created_at)}
                  </div>

                  {ticket.category && (
                    <div className="flex items-center text-sm text-gray-600">
                      <AlertCircle className="w-4 h-4 mr-2" />
                      {ticket.category}
                    </div>
                  )}

                  {ticket.from_whatsapp && (
                    <div className="flex items-center mt-1">
                      <Badge
                        variant="outline"
                        className="text-green-600 border-green-600"
                      >
                        📱 Via WhatsApp
                      </Badge>
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <Button
                    onClick={() => handleTakeTicket(ticket.id)}
                    disabled={loading}
                    className="w-full"
                    data-testid={`take-ticket-${ticket.protocol}`}
                  >
                    {takingTicket === ticket.id
                      ? "Iniciando..."
                      : "Assumir Atendimento"}
                  </Button>

                  <Button
                    onClick={() => navigate(`/chat/${ticket.id}`)}
                    variant="outline"
                    className="w-full"
                    data-testid={`view-ticket-${ticket.protocol}`}
                  >
                    <Search className="w-4 h-4" />
                    Visualizar Atendimento
                  </Button>

                  {externalApiEnabled && !ticket.client_identified && (
                    <Button
                      onClick={() => openLinkClientModal(ticket.id)}
                      variant="outline"
                      className="w-full border-purple-300 text-purple-700 hover:bg-purple-50"
                      data-testid={`link-client-${ticket.protocol}`}
                    >
                      <Link2 className="w-4 h-4" />
                      Vincular Cliente
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showLinkClientModal} onOpenChange={setShowLinkClientModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-purple-600" />
              Vincular Cliente ao Atendimento
            </DialogTitle>
            <DialogDescription>
              Selecione um cliente da API externa para vincular a este
              atendimento.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar por razão social, nome fantasia, CNPJ, WhatsApp..."
                value={linkClientSearch}
                onChange={(e) => setLinkClientSearch(e.target.value)}
                className="pl-10"
                data-testid="link-client-search-input"
              />
            </div>

            <div className="h-64 overflow-y-auto border rounded-lg bg-gray-50">
              {loadingClients ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
                  <span className="ml-2 text-gray-500">
                    Carregando clientes...
                  </span>
                </div>
              ) : filteredLinkClients.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  {linkClientSearch
                    ? "Nenhum cliente encontrado"
                    : "Nenhum cliente disponível"}
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredLinkClients.slice(0, 100).map((client) => (
                    <div
                      key={client.id}
                      onClick={() => setLinkSelectedClient(client)}
                      className={`p-3 cursor-pointer transition-all duration-150 hover:bg-white ${
                        linkSelectedClient?.id === client.id
                          ? "bg-purple-50 border-l-4 border-purple-500 shadow-sm"
                          : "hover:shadow-sm"
                      }`}
                      data-testid={`link-client-option-${client.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 truncate">
                            {client.razao_social || client.name || "Cliente"}
                          </p>

                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            {client.nome_fantasia && (
                              <span className="text-sm text-blue-600 font-medium">
                                {client.nome_fantasia}
                              </span>
                            )}
                            {client.cnpj && (
                              <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                                {client.cnpj}
                              </span>
                            )}
                          </div>

                          {(client.whatsapp || client.phone) && (
                            <div className="flex items-center gap-1 mt-1 text-sm text-green-600">
                              <Phone className="w-3 h-3" />
                              <span>{client.whatsapp || client.phone}</span>
                            </div>
                          )}
                        </div>

                        {linkSelectedClient?.id === client.id && (
                          <Badge className="bg-purple-100 text-purple-700 shrink-0">
                            ✓ Selecionado
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {filteredLinkClients.length > 100 && (
              <p className="text-xs text-gray-500 text-center">
                Mostrando 100 de {filteredLinkClients.length} resultados. Refine
                sua busca para ver mais.
              </p>
            )}

            {linkSelectedClient && (
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                <p className="text-sm font-medium text-purple-800">
                  ✅ Cliente selecionado:{" "}
                  <strong>
                    {linkSelectedClient.razao_social || linkSelectedClient.name}
                  </strong>
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowLinkClientModal(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => handleLinkClient()}
              disabled={linkingClient || !linkSelectedClient}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="confirm-link-client-btn"
            >
              {linkingClient ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Vinculando...
                </>
              ) : (
                <>
                  <Link2 className="w-4 h-4 mr-2" />
                  Vincular Cliente
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ClientNameChoiceDialog
        open={showClientNameChoice}
        onOpenChange={setShowClientNameChoice}
        currentName={linkingClientName}
        companyName={getExternalClientName(linkSelectedClient)}
        onConfirm={handleLinkClient}
      />
    </div>
  );
};

export default Queue;
